import type { GameState, Passenger } from '@full-elevator/sim';
import { deadlineFraction, deadlineRemaining, patienceRemaining } from '@full-elevator/sim';
import { Container, Graphics, Text } from 'pixi.js';
import { COLORS, DISPLAY_FONT } from '../theme.js';

/**
 * Answers on the spot (v0.8, after the AI tester's review): a ring where a tap
 * landed, so a tap that changes nothing visible still reads as taken, and what
 * a stop earned or cost, rising off it, on levels graded on service.
 *
 * View only. The points shown are the sim's own (`valueOfDelivery`,
 * `costOfLoss`), never worked out here.
 */

/** How long a tap's ring and a stop's score last, in ms. */
export const RIPPLE_MS = 380;
export const FLOAT_MS = 1200;
/** Deliveries at one stop this close together are one score, added up. */
const MERGE_MS = 450;

function clamp01(t: number): number {
  return Math.min(1, Math.max(0, t));
}

/** A tap's ring, `t` (0..1) of the way through: growing quickly, fading. */
export function ripple(t: number): { radius: number; alpha: number } {
  const p = clamp01(t);
  return { radius: 6 + 20 * (1 - (1 - p) ** 2), alpha: 0.55 * (1 - p) };
}

/** A score rising off the stop: up and slowing, then fading in its last third. */
export function floatPose(t: number): { dy: number; alpha: number } {
  const p = clamp01(t);
  return { dy: -22 * (1 - (1 - p) ** 2), alpha: p < 0.65 ? 1 : 1 - (p - 0.65) / 0.35 };
}

/** "+85", "−120": what a stop earned or cost, as it is shown. */
export function scoreText(points: number): string {
  return points >= 0 ? `+${points}` : `−${Math.abs(points)}`;
}

/**
 * Somebody whose clock is past half: patience for the impatient, the deadline
 * for a courier. On a run with no losses to spare, they are the ones about to
 * end it, and they say so (`lossesToSpare` in the sim decides the "no spare").
 */
export function runningOut(state: GameState, passenger: Passenger): boolean {
  if (passenger.state !== 'waiting' && passenger.state !== 'riding') return false;
  const patience = patienceRemaining(state, passenger);
  if (patience !== null && passenger.patienceMs !== null && patience < passenger.patienceMs / 2) {
    return true;
  }
  const deadline = deadlineFraction(state, passenger);
  return deadline !== null && deadline < 0.5;
}

/**
 * The last seconds of someone's clock: their patience waiting, or their
 * deadline anywhere. In a crowd a bar running out went unseen (the owner,
 * PLAYTEST_LOG 2026-10-29), so the ticket blinks and a tick sounds once.
 */
export const LAST_SECONDS_MS = 10_000;

export function inLastSeconds(state: GameState, passenger: Passenger): boolean {
  const left = [patienceRemaining(state, passenger), deadlineRemaining(state, passenger)].filter(
    (ms): ms is number => ms !== null,
  );
  return left.length > 0 && Math.min(...left) < LAST_SECONDS_MS;
}

/** A blink for a ticket in its last seconds: full, then faint, about twice a second. */
export function blinkAlpha(ms: number): number {
  return 0.6 + 0.4 * Math.cos(ms / 80);
}

/** A small warning sign at a ticket's corner: this one, if lost, ends the run. */
export function paintWarning(g: Graphics, x: number, y: number, ms: number): void {
  const r = 6 + Math.sin(ms / 170) * 0.8;
  g.circle(x, y, r).fill({ color: COLORS.cabin }).stroke({ width: 1.5, color: COLORS.cabinFull });
  g.roundRect(x - 1, y - 4, 2, 5, 1).fill({ color: COLORS.cabinFull });
  g.circle(x, y + 3, 1.1).fill({ color: COLORS.cabinFull });
}

/** How long a cheer lasts, in ms, and how close together a stop's arrivals count as one stop. */
export const CHEER_MS = 1300;
export const STOP_WINDOW_MS = 1500;

/**
 * A word for a stop where several got out at once (ADR-0037): the feeling of
 * a good stop, said. Presentation only — it earns nothing the sim did not.
 */
export function cheerFor(outAtOnce: number): string | null {
  if (outAtOnce >= 5) return 'Full service!';
  if (outAtOnce === 4) return 'Express!';
  if (outAtOnce === 3) return 'Smooth!';
  return null;
}

/** Every fifth delivery in a row with nobody lost along the way. */
export function streakCheer(inARow: number): string | null {
  return inARow > 0 && inARow % 5 === 0 ? `${inARow} in a row!` : null;
}

/** A cheer popping up, `t` (0..1) through: it swells past full size, settles, rises and fades. */
export function cheerPose(t: number): { scale: number; dy: number; alpha: number } {
  const p = clamp01(t);
  const scale =
    p < 0.15 ? 0.6 + (p / 0.15) * 0.55 : p < 0.3 ? 1.15 - ((p - 0.15) / 0.15) * 0.15 : 1;
  return { scale, dy: -30 * p, alpha: p < 0.7 ? 1 : 1 - (p - 0.7) / 0.3 };
}

interface Cheer {
  readonly text: Text;
  readonly key: string;
  readonly x: number;
  readonly y: number;
  at: number;
}

/** Someone walking off: a loss goes with them rather than staying where they stood. */
export interface Walk {
  /** How far they go, in px, and how long they take. */
  readonly dx: number;
  readonly overMs: number;
}

interface Floater {
  readonly text: Text;
  readonly key: string;
  readonly x: number;
  readonly y: number;
  /** When it appeared: a walk is timed from here, however often it was added to. */
  readonly born: number;
  readonly walk: Walk | null;
  at: number;
  points: number;
}

/** How far along its walk a score is, `ms` after it appeared. */
export function walked(walk: Walk | null, ms: number): number {
  if (!walk) return 0;
  return walk.dx * clamp01(ms / Math.max(1, walk.overMs));
}

export class Feedback {
  readonly layer = new Container();
  private readonly rings = new Graphics();
  private ripples: { x: number; y: number; at: number }[] = [];
  private floats: Floater[] = [];
  private cheers: Cheer[] = [];

  constructor(private readonly still: boolean) {
    this.layer.addChild(this.rings);
  }

  /** A tap landed here. */
  tap(x: number, y: number, now: number): void {
    this.ripples.push({ x, y, at: now });
  }

  /**
   * What a stop earned (or, negative, cost). `key` keeps one score per stop. A
   * score that walks is centred over the one it is about and goes with them.
   */
  score(
    points: number,
    key: string,
    x: number,
    y: number,
    now: number,
    walk: Walk | null = null,
  ): void {
    const recent = this.floats.find((f) => f.key === key && now - f.at < MERGE_MS);
    if (recent) {
      recent.points += points;
      recent.at = now;
      this.style(recent);
      return;
    }
    const text = new Text({
      text: '',
      style: {
        fontFamily: DISPLAY_FONT,
        fontWeight: '800',
        fontSize: 15,
        fill: COLORS.text,
        stroke: { color: COLORS.background, width: 4 },
      },
    });
    text.anchor.set(walk ? 0.5 : 0, 0.5);
    const floater = { text, key, x, y, born: now, walk, at: now, points };
    this.style(floater);
    this.layer.addChild(text);
    this.floats.push(floater);
  }

  /** A word over a stop (ADR-0037). A newer one with the same key takes its place. */
  cheer(word: string, key: string, x: number, y: number, now: number): void {
    const old = this.cheers.find((c) => c.key === key);
    if (old) {
      old.text.destroy();
      this.cheers = this.cheers.filter((c) => c !== old);
    }
    const text = new Text({
      text: word,
      style: {
        fontFamily: DISPLAY_FONT,
        fontWeight: '800',
        fontSize: 22,
        fill: COLORS.target,
        stroke: { color: COLORS.background, width: 5 },
      },
    });
    text.anchor.set(0.5);
    this.layer.addChild(text);
    this.cheers.push({ text, key, x, y, at: now });
  }

  private style(f: Floater): void {
    f.text.text = scoreText(f.points);
    f.text.style.fill = f.points >= 0 ? COLORS.text : COLORS.cabinFull;
  }

  update(now: number): void {
    this.rings.clear();
    this.ripples = this.ripples.filter((r) => now - r.at < RIPPLE_MS);
    for (const r of this.ripples) {
      const ring = ripple((now - r.at) / RIPPLE_MS);
      this.rings.circle(r.x, r.y, ring.radius).stroke({
        width: 2.5,
        color: COLORS.target,
        alpha: ring.alpha,
      });
    }
    for (const f of this.floats) {
      const pose = floatPose((now - f.at) / FLOAT_MS);
      const x = f.x + (this.still ? 0 : walked(f.walk, now - f.born));
      f.text.position.set(x, f.y + (this.still ? 0 : pose.dy));
      f.text.alpha = pose.alpha;
    }
    for (const c of this.cheers) {
      const pose = cheerPose((now - c.at) / CHEER_MS);
      c.text.position.set(c.x, c.y + (this.still ? 0 : pose.dy));
      c.text.scale.set(this.still ? 1 : pose.scale);
      c.text.alpha = pose.alpha;
    }
    for (const c of this.cheers.filter((c) => now - c.at >= CHEER_MS)) c.text.destroy();
    this.cheers = this.cheers.filter((c) => now - c.at < CHEER_MS);
    const done = this.floats.filter((f) => now - f.at >= FLOAT_MS);
    for (const f of done) f.text.destroy();
    this.floats = this.floats.filter((f) => now - f.at < FLOAT_MS);
  }
}
